import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { SendHorizontal } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Textarea } from "~/components/ui/textarea";
import { useIsMobile } from "~/hooks/use-mobile";
import { api } from "~/lib/api";
import { subjectFrom, useConversation } from "~/lib/conversation";
import { formatDate, toNum } from "~/lib/format";
import { cn } from "~/lib/utils";

type Viewer = "customer" | "ops";

const REFRESH_AFTER_SEND: Record<Viewer, string[][]> = {
  customer: [["my-unread"], ["my-tickets"]],
  ops: [["ops", "unread"], ["ops", "conversations"]],
};

async function deliver(viewer: Viewer, parcelId: string, ticketId: string | null, text: string): Promise<void> {
  if (viewer === "customer") {
    if (ticketId) await api.replyTicket(parcelId, ticketId, text);
    else await api.openTicket(parcelId, subjectFrom(text), text);
    return;
  }
  if (ticketId) await api.ops.replyTicket(ticketId, text);
  else await api.ops.openTicket(parcelId, subjectFrom(text), text);
}

export function Conversation({
  parcelId,
  viewer,
  emptyText,
  label,
  placeholder,
  compact = false,
  fill = false,
}: {
  parcelId: string;
  viewer: Viewer;
  emptyText: string;
  label: string;
  placeholder: string;
  compact?: boolean;
  fill?: boolean;
}) {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const listRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const conversation = useConversation(parcelId);
  const entries = conversation.data?.entries ?? [];
  const mine = (role?: string | null) => (viewer === "customer" ? role === "customer" : role !== "customer");

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [entries.length, sending]);

  async function send() {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(text);
    setDraft("");
    setError(null);
    try {
      await deliver(viewer, parcelId, conversation.data?.latestTicketId ?? null, text);
      await qc.invalidateQueries({ queryKey: ["conversation", parcelId] });
      for (const queryKey of REFRESH_AFTER_SEND[viewer]) void qc.invalidateQueries({ queryKey });
    } catch {
      setDraft(text);
      setError("Your message was not sent. Check your connection and try again.");
    } finally {
      setSending(null);
    }
  }

  const empty = entries.length === 0 && !sending;

  return (
    <div className={cn(fill && "flex min-h-0 flex-1 flex-col")}>
      <div
        ref={listRef}
        aria-live="polite"
        className={cn("space-y-3 overflow-y-auto", fill ? "min-h-0 flex-1 p-4" : "max-h-96", empty && !fill && "hidden")}
      >
        {empty && fill && <p className="text-text-faint py-8 text-center text-[13px]">{emptyText}</p>}
        {entries.map(({ message, topic }) => (
          <div key={toNum(message.id)}>
            {topic && <p className="text-muted-foreground mb-2 text-center text-[12px]">{topic}</p>}
            <Bubble
              mine={mine(message.authorRole)}
              author={mine(message.authorRole) ? undefined : viewer === "customer" ? "ParcelPoint" : "Customer"}
              body={message.body ?? ""}
              caption={formatDate(message.createdAt)}
              compact={compact}
            />
          </div>
        ))}
        {sending && <Bubble mine body={sending} caption="Sending…" compact={compact} pending />}
      </div>

      {empty && !fill && (
        <p className={cn("text-muted-foreground", compact ? "text-[13px]" : "type-body")}>
          {conversation.isError ? "Messages did not load. Reload the page to try again." : emptyText}
        </p>
      )}

      <div className={cn(fill ? "border-hairline border-t p-3" : "mt-4")}>
        <div className="flex items-end gap-2">
          <Textarea
            aria-label={label}
            placeholder={placeholder}
            rows={1}
            value={draft}
            className={cn(
              "max-h-36 flex-1 resize-none",
              compact ? "min-h-9 px-2.5 py-2 text-[13px] max-md:min-h-11 max-md:text-[16px]" : "min-h-12 rounded-lg px-3.5 py-3 text-[16px]",
            )}
            onChange={(e) => {
              setDraft(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !isMobile) {
                e.preventDefault();
                void send();
              }
            }}
          />
          <Button
            size={compact ? "default" : "touch"}
            className={cn("shrink-0 px-0", compact ? "w-9 max-md:size-11" : "w-12")}
            aria-label="Send message"
            disabled={!draft.trim() || !!sending}
            onClick={() => void send()}
          >
            <SendHorizontal className={compact ? "size-4" : "size-5"} />
          </Button>
        </div>
        {error && (
          <p role="alert" className="text-destructive type-footnote mt-2">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

function Bubble({
  mine,
  author,
  body,
  caption,
  compact,
  pending,
}: {
  mine: boolean;
  author?: string;
  body: string;
  caption: string;
  compact: boolean;
  pending?: boolean;
}) {
  return (
    <div className={cn("flex", mine ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-[82%] rounded-2xl",
          compact ? "px-3 py-2" : "px-3.5 py-2.5",
          mine ? "bg-primary text-primary-foreground rounded-br-md" : "bg-muted rounded-bl-md",
          pending && "opacity-60",
        )}
      >
        {author && <p className="mb-0.5 text-[12px] font-semibold">{author}</p>}
        <p className={cn("leading-snug wrap-anywhere whitespace-pre-wrap", compact ? "text-[13px]" : "text-[15px]")}>{body}</p>
        <p className={cn("mt-1", compact ? "text-[11px]" : "text-[12px]", mine ? "text-primary-foreground/70" : "text-muted-foreground")}>
          {caption}
        </p>
      </div>
    </div>
  );
}
